import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { api } from '@/lib/api';
import { Owner } from '@/lib/types';
import { SOURCE_LABELS, getInitials } from '@/lib/utils';
import Modal from '@/components/ui/Modal';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import OwnerForm from '@/components/owners/OwnerForm';
import { Plus, Search, Phone, Mail, MessageCircle } from 'lucide-react';

export default function OwnersPage() {
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  // Por defecto solo los activos; «Todos los estados» sigue en el desplegable
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);

  const params: Record<string, string> = {};
  if (search) params.search = search;
  if (sourceFilter) params.source = sourceFilter;
  if (statusFilter) params.status = statusFilter;

  const { data: owners, loading, refresh } = useApi<Owner[]>(
    () => api.getOwners(Object.keys(params).length > 0 ? params : undefined),
    [search, sourceFilter, statusFilter]
  );

  const navigate = useNavigate();

  const handleCreate = async (data: any) => {
    await api.createOwner(data);
    setShowForm(false);
    refresh();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 gap-3">
        <h1 className="text-xl sm:text-2xl font-bold">Propietarios</h1>
        <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2 text-sm sm:text-base">
          <Plus size={18} />
          <span className="hidden sm:inline">Nuevo propietario</span>
          <span className="sm:hidden">Nuevo</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
            <input
              type="text"
              placeholder="Buscar por nombre, email, teléfono..."
              className="input pl-9"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select className="input w-auto" value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}>
            <option value="">Todas las fuentes</option>
            {Object.entries(SOURCE_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
          <select className="input w-auto" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="inactive">Inactivos</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <LoadingSpinner />
      ) : !owners || owners.length === 0 ? (
        <EmptyState
          title="No hay propietarios"
          description="Añade tu primer propietario para empezar"
          action={
            <button onClick={() => setShowForm(true)} className="btn-primary">
              <Plus size={16} className="inline mr-1" /> Nuevo propietario
            </button>
          }
        />
      ) : (
        <div className="card overflow-hidden p-0">
          {/* Desktop table */}
          <table className="w-full hidden md:table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Nombre</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Contacto</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Estado</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Propiedades</th>
                <th className="text-left text-xs font-semibold text-sereno-dark/50 uppercase tracking-wider px-6 py-3">Modelo</th>
              </tr>
            </thead>
            <tbody>
              {owners.map(owner => (
                <tr
                  key={owner.id}
                  onClick={() => navigate(`/owners/${owner.id}`)}
                  className="border-b border-gray-50 hover:bg-sereno-blue/20 cursor-pointer transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-sereno-dark text-white flex items-center justify-center text-xs font-medium">
                        {getInitials(owner.firstName, owner.lastName)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{owner.firstName} {owner.lastName}</p>
                        {owner.dni && <p className="text-xs text-sereno-dark/40">{owner.dni}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {owner.phone && (
                        <a href={`tel:${owner.phone}`} onClick={e => e.stopPropagation()} className="text-sereno-dark/40 hover:text-sereno-dark">
                          <Phone size={14} />
                        </a>
                      )}
                      {owner.email && (
                        <a href={`mailto:${owner.email}`} onClick={e => e.stopPropagation()} className="text-sereno-dark/40 hover:text-sereno-dark">
                          <Mail size={14} />
                        </a>
                      )}
                      {owner.whatsapp && (
                        <a href={`https://wa.me/${owner.whatsapp.replace(/\D/g, '')}`} onClick={e => e.stopPropagation()} target="_blank" rel="noopener noreferrer" className="text-sereno-dark/40 hover:text-green-600">
                          <MessageCircle size={14} />
                        </a>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`badge ${owner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {owner.status === 'active' ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {(owner as any).propertyNames?.length > 0
                      ? <div className="flex flex-wrap gap-1">
                          {(owner as any).propertyNames.map((name: string) => (
                            <span key={name} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-sereno-blue/30 text-sereno-dark">
                              {name}
                            </span>
                          ))}
                        </div>
                      : <span className="text-sereno-dark/30">-</span>}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {owner.contractModel
                      ? <span className={`badge ${{
                          gestion: 'bg-blue-100 text-blue-700',
                          explotacion: 'bg-amber-100 text-amber-700',
                          explotacion_lcm: 'bg-amber-100 text-amber-700',
                          explotacion_sin_irpf: 'bg-amber-100 text-amber-700',
                          rent2rent: 'bg-purple-100 text-purple-700',
                          ambos: 'bg-gray-100 text-gray-700',
                        }[owner.contractModel] ?? 'bg-gray-100 text-gray-700'}`}>
                          {{
                            gestion: 'Gestión',
                            explotacion: 'Explotación',
                            explotacion_lcm: 'Explotación LCM',
                            explotacion_sin_irpf: 'Explotación s/IRPF',
                            rent2rent: 'Rent2Rent',
                            ambos: 'Ambos',
                          }[owner.contractModel] ?? owner.contractModel}
                        </span>
                      : <span className="text-sereno-dark/30">-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-gray-50">
            {owners.map(owner => (
              <div
                key={owner.id}
                onClick={() => navigate(`/owners/${owner.id}`)}
                className="p-4 hover:bg-sereno-blue/20 cursor-pointer transition-colors active:bg-sereno-blue/30"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sereno-dark text-white flex items-center justify-center text-xs font-medium flex-shrink-0">
                    {getInitials(owner.firstName, owner.lastName)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm truncate">{owner.firstName} {owner.lastName}</p>
                      <span className={`badge text-[10px] ${owner.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {owner.status === 'active' ? 'Activo' : 'Inactivo'}
                      </span>
                    </div>
                    <p className="text-xs text-sereno-dark/40 truncate">{owner.email || owner.phone || '-'}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {owner.phone && (
                      <a href={`tel:${owner.phone}`} onClick={e => e.stopPropagation()} className="text-sereno-dark/40 hover:text-sereno-dark p-1">
                        <Phone size={16} />
                      </a>
                    )}
                    {owner.whatsapp && (
                      <a href={`https://wa.me/${owner.whatsapp.replace(/\D/g, '')}`} onClick={e => e.stopPropagation()} target="_blank" rel="noopener noreferrer" className="text-sereno-dark/40 hover:text-green-600 p-1">
                        <MessageCircle size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title="Nuevo propietario" size="lg">
        <OwnerForm onSubmit={handleCreate} onCancel={() => setShowForm(false)} />
      </Modal>
    </div>
  );
}
